import type { AdCreativeItem } from '@segmently/schema-models/ad-creative';
import {
  findCreativeLocaleKey,
  getCreativeLocaleStatus,
  isSegmentlyOwnedCreativeItem,
} from '../../services/creativeLocaleProjection';

const MAX_PIN_LENGTH = 512;
const CONTROL_CHARACTERS = /[\u0000-\u001F\u007F]/;
const SHA_256_HEX = /^[a-f0-9]{64}$/;
const ALLOWED_QUERY_KEYS = new Set([
  'view',
  'intent',
  'intentVersion',
  'sourceKind',
  'sourceCreativeItem',
  'sourceExportManifest',
  'sourceExportedManifestHash',
  'sourceRendition',
  'sourceLocale',
  'sourceDeployment',
  'sourceProvider',
  'sourceProviderAccount',
  'sourceProviderResource',
  'sourceProviderMutationAllowed',
  'sourceReviewRequired',
  'sourceLocalizationVariant',
  // Added by the explicit "Open exact source for review" action. They keep
  // the immutable pins attached while the batch drawer is open.
  'batch',
  'step',
  'item',
]);

export type LocalizedVariationSourceProvider = 'meta' | 'google_ads' | 'tiktok_ads';

export interface LocalizedVariationHandoff {
  schemaVersion: 1;
  projectId: string;
  sourceKind: 'exact-deployment-export';
  creativeItemId: string;
  exportManifestId: string;
  exportedManifestHash: string;
  renditionId: string;
  locale: string;
  deploymentKey: string;
  provider: LocalizedVariationSourceProvider;
  providerAccountId: string;
  providerResourceName: string;
  localizationVariantId?: string;
  providerMutationAllowed: false;
  reviewRequired: true;
}

export type LocalizedVariationHandoffParseResult =
  | { status: 'not_requested' }
  | { status: 'invalid'; code: 'invalid_localized_variation_handoff'; message: string }
  | { status: 'valid'; handoff: LocalizedVariationHandoff };

export type LocalizedVariationHandoffResolution =
  | { status: 'available'; item: AdCreativeItem; locale: string }
  | {
    status: 'unavailable';
    code: 'source_item_unavailable';
    message: string;
  };

/**
 * Strict, read-only boundary for Funnel Insights -> Ad Creative links.
 * The URL is evidence for review only. Parsing it never creates an authored
 * child and never calls a provider API.
 */
export function parseLocalizedVariationHandoff(
  params: URLSearchParams,
  projectId: string,
): LocalizedVariationHandoffParseResult {
  if (!params.has('intent')) {
    return { status: 'not_requested' };
  }

  try {
    for (const key of params.keys()) {
      if (!ALLOWED_QUERY_KEYS.has(key)) {
        fail('The localized variation handoff contains unsupported query parameters.');
      }
    }

    const scopedProjectId = strictPin(projectId, 'project route');
    if (requiredSingle(params, 'view') !== 'library') fail('view must be library.');
    if (requiredSingle(params, 'intent') !== 'start-localized-variation') {
      fail('intent must be start-localized-variation.');
    }
    if (requiredSingle(params, 'intentVersion') !== '1') fail('intentVersion must be 1.');
    if (requiredSingle(params, 'sourceKind') !== 'exact-deployment-export') {
      fail('sourceKind must be exact-deployment-export.');
    }
    if (requiredSingle(params, 'sourceProviderMutationAllowed') !== 'false') {
      fail('sourceProviderMutationAllowed must be false.');
    }
    if (requiredSingle(params, 'sourceReviewRequired') !== 'true') {
      fail('sourceReviewRequired must be true.');
    }

    const provider = requiredSingle(params, 'sourceProvider');
    if (!isSourceProvider(provider)) fail('sourceProvider is not supported.');
    const locale = requiredSingle(params, 'sourceLocale');
    assertLocale(locale);

    const exportedManifestHash = requiredSingle(params, 'sourceExportedManifestHash').toLowerCase();
    if (!SHA_256_HEX.test(exportedManifestHash)) {
      fail('sourceExportedManifestHash must be a SHA-256 hex digest.');
    }

    const creativeItemId = requiredSingle(params, 'sourceCreativeItem');
    assertOptionalReviewWorkspaceRoute(params, creativeItemId);

    return {
      status: 'valid',
      handoff: {
        schemaVersion: 1,
        projectId: scopedProjectId,
        sourceKind: 'exact-deployment-export',
        creativeItemId,
        exportManifestId: requiredSingle(params, 'sourceExportManifest'),
        exportedManifestHash,
        renditionId: requiredSingle(params, 'sourceRendition'),
        locale,
        deploymentKey: requiredSingle(params, 'sourceDeployment'),
        provider,
        providerAccountId: requiredSingle(params, 'sourceProviderAccount'),
        providerResourceName: requiredSingle(params, 'sourceProviderResource'),
        localizationVariantId: optionalSingle(params, 'sourceLocalizationVariant'),
        providerMutationAllowed: false,
        reviewRequired: true,
      },
    };
  } catch (error) {
    return {
      status: 'invalid',
      code: 'invalid_localized_variation_handoff',
      message: error instanceof Error ? error.message : 'The localized variation handoff is invalid.',
    };
  }
}

/** Resolve only one exact, current-project, strict Segmently-authored item. */
export function resolveLocalizedVariationHandoffItem(
  handoff: LocalizedVariationHandoff,
  items: readonly AdCreativeItem[],
): LocalizedVariationHandoffResolution {
  const matches = items.filter((candidate) => candidate.id === handoff.creativeItemId);
  if (matches.length !== 1) return unavailable();
  const item = matches[0];
  if (item.projectId !== handoff.projectId || !isSegmentlyOwnedCreativeItem(item) || !item.briefId) {
    return unavailable();
  }
  if (!providerMatchesNetwork(handoff.provider, item.networkId)) return unavailable();
  const locale = findCreativeLocaleKey(item, handoff.locale);
  if (!locale || ['missing', 'archived'].includes(getCreativeLocaleStatus(item, locale))) {
    return unavailable();
  }
  return { status: 'available', item, locale };
}

function requiredSingle(params: URLSearchParams, key: string): string {
  const values = params.getAll(key);
  if (values.length !== 1) fail(`${key} must appear exactly once.`);
  return strictPin(values[0], key);
}

function optionalSingle(params: URLSearchParams, key: string): string | undefined {
  const values = params.getAll(key);
  if (values.length > 1) fail(`${key} must appear at most once.`);
  return values.length === 1 ? strictPin(values[0], key) : undefined;
}

function strictPin(value: string | undefined, field: string): string {
  if (!value || value.length > MAX_PIN_LENGTH || CONTROL_CHARACTERS.test(value) || value !== value.trim()) {
    fail(`${field} is missing or invalid.`);
  }
  return value;
}

function assertLocale(locale: string): void {
  try {
    if (new Intl.Locale(locale).baseName !== locale) fail('sourceLocale must be canonical.');
  } catch {
    fail('sourceLocale must be a valid canonical locale.');
  }
}

function assertOptionalReviewWorkspaceRoute(params: URLSearchParams, creativeItemId: string): void {
  const batchId = optionalSingle(params, 'batch');
  const step = optionalSingle(params, 'step');
  const itemId = optionalSingle(params, 'item');
  const hasWorkspaceRoute = batchId !== undefined || step !== undefined || itemId !== undefined;
  if (!hasWorkspaceRoute) return;
  if (!batchId || step !== 'creatives' || itemId !== creativeItemId) {
    fail('The review workspace route does not match the pinned source creative.');
  }
}

function isSourceProvider(value: string): value is LocalizedVariationSourceProvider {
  return value === 'meta' || value === 'google_ads' || value === 'tiktok_ads';
}

function providerMatchesNetwork(provider: LocalizedVariationSourceProvider, networkId: string): boolean {
  if (provider === 'meta') return networkId === 'meta';
  if (provider === 'google_ads') return networkId === 'google_ads' || networkId === 'google';
  return networkId === 'tiktok_ads' || networkId === 'tiktok';
}

function unavailable(): LocalizedVariationHandoffResolution {
  return {
    status: 'unavailable',
    code: 'source_item_unavailable',
    message: 'The exact Segmently source creative or pinned locale is not available in this project.',
  };
}

function fail(message: string): never {
  throw new Error(message);
}
